/**
 * ComposeEmailModal — Full email compose, reply, and forward modal.
 * Uses TipTap rich text editor for HTML email composition.
 * Sends via Microsoft Graph through backend API.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X,
  Send,
  Save,
  Loader2,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Reply,
  ReplyAll,
  Forward,
  Trash2,
  FileText,
  Sparkles,
  Wand2,
  Eye,
  MousePointerClick,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { RichTextEditor } from '../../web-presence/components/RichTextEditor/RichTextEditor';

// ============================================================================
// TYPES
// ============================================================================

interface Activity {
  id: string;
  type: string;
  title: string;
  description?: string;
  direction?: 'inbound' | 'outbound';
  opportunityId?: string;
  organizationId?: number;
  emailId?: string;
  metadata?: {
    from_email?: string;
    to_emails?: string[];
    cc_emails?: string[];
    provider?: string;
    folder?: string;
  };
}

interface Opportunity {
  id: string;
  name: string;
  company: string;
}

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

export interface ComposeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: ComposeMode;
  sourceActivity?: Activity | null;
  opportunities?: Opportunity[];
  onSent?: (activityId: string) => void;
  defaultTo?: string[];
  defaultSubject?: string;
  defaultOpportunityId?: string;
}

interface EmailSuggestion {
  email: string;
  name: string;
  source: string;
}

interface EmailTemplate {
  id: number;
  name: string;
  subject?: string;
  bodyHtml?: string;
  category?: string;
  isShared?: boolean;
}

// ============================================================================
// RECIPIENT INPUT COMPONENT (with auto-complete)
// ============================================================================

function RecipientInput({
  label,
  emails,
  onChange,
  placeholder,
}: {
  label: string;
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsDisabled = useRef(false);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2 || suggestionsDisabled.current) { setSuggestions([]); return; }
    try {
      const res = await apiClient.get('/api/crm-activities/email-suggestions', { params: { q } });
      setSuggestions(res.data?.suggestions || []);
      setShowSuggestions(true);
      setSelectedIdx(-1);
    } catch {
      setSuggestions([]);
      // Disable auto-complete if endpoint doesn't exist yet (405/404)
      suggestionsDisabled.current = true;
    }
  }, []);

  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const addEmail = (email: string) => {
    if (email && !emails.includes(email)) {
      onChange([...emails, email]);
    }
    setInputValue('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' && selectedIdx >= 0) { e.preventDefault(); addEmail(suggestions[selectedIdx].email); return; }
    }
    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && inputValue.trim()) {
      e.preventDefault();
      addEmail(inputValue.trim().replace(/,$/,''));
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      onChange(emails.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const pasted = text.split(/[,;\s]+/).filter(s => s.includes('@'));
    if (pasted.length > 0) {
      const unique = [...new Set([...emails, ...pasted])];
      onChange(unique);
    }
  };

  const removeEmail = (idx: number) => {
    onChange(emails.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 relative">
      <label className="text-xs font-medium text-gray-500 w-8 pt-1.5 flex-shrink-0">{label}</label>
      <div className="flex-1 flex flex-wrap items-center gap-1 min-h-[28px]">
        {emails.map((email, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
          >
            {email}
            <button onClick={() => removeEmail(idx)} className="hover:text-blue-900">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          onBlur={() => {
            setTimeout(() => setShowSuggestions(false), 200);
            if (inputValue.trim() && inputValue.includes('@')) {
              addEmail(inputValue.trim());
            }
          }}
          placeholder={emails.length === 0 ? (placeholder || 'Add recipients...') : ''}
          className="flex-1 min-w-[120px] text-sm outline-none bg-transparent py-0.5"
        />
      </div>

      {/* Auto-complete suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-8 top-full mt-1 w-[calc(100%-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
          {suggestions.map((s, idx) => (
            <button
              key={`${s.email}-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); addEmail(s.email); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between ${
                idx === selectedIdx ? 'bg-blue-50' : ''
              }`}
            >
              <div>
                <span className="font-medium text-gray-900">{s.name}</span>
                <span className="text-gray-500 ml-2">{s.email}</span>
              </div>
              <span className="text-[10px] text-gray-400">{s.source}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ComposeEmailModal({
  isOpen,
  onClose,
  mode,
  sourceActivity,
  opportunities = [],
  onSent,
  defaultTo = [],
  defaultSubject = '',
  defaultOpportunityId = '',
}: ComposeEmailModalProps) {
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [opportunityId, setOpportunityId] = useState('');
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Template state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');

  // AI assist state
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState<'professional' | 'friendly' | 'formal'>('professional');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');

  // Tracking state
  const [trackingEnabled, setTrackingEnabled] = useState(true);

  // Load templates lazily when picker is opened
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (templatesLoaded) return;
    try {
      const res = await apiClient.get('/api/crm-activities/email-templates');
      setTemplates(res.data?.templates || []);
      setTemplatesLoaded(true);
    } catch {
      // Templates endpoint may not be deployed yet — silently ignore
    }
  }, [templatesLoaded]);

  // Reset loaded state when modal closes
  useEffect(() => {
    if (!isOpen) setTemplatesLoaded(false);
  }, [isOpen]);

  const applyTemplate = (t: EmailTemplate) => {
    if (t.subject) setSubject(t.subject);
    if (t.bodyHtml) setBodyHtml(t.bodyHtml);
    setShowTemplates(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      await apiClient.post('/api/crm-activities/email-templates', {
        name: templateName,
        subject,
        body_html: bodyHtml,
        category: templateCategory || undefined,
        is_shared: false,
      });
      setSaveTemplateOpen(false);
      setTemplateName('');
      setTemplateCategory('');
      // Reload templates
      setTemplatesLoaded(false);
      try {
        const res = await apiClient.get('/api/crm-activities/email-templates');
        setTemplates(res.data?.templates || []);
        setTemplatesLoaded(true);
      } catch {}
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleAICompose = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await apiClient.post('/api/crm-activities/ai/compose', {
        prompt: aiPrompt,
        tone: aiTone,
        subject: subject || undefined,
        opportunity_id: opportunityId || undefined,
        to_emails: to,
      });
      setAiResult(res.data?.body_html || res.data?.body || '');
    } catch (err: any) {
      setAiResult(`AI compose unavailable: ${err?.response?.data?.detail || err?.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  const insertAIResult = () => {
    setBodyHtml(aiResult);
    setShowAI(false);
    setAiResult('');
    setAiPrompt('');
  };

  // Initialize fields based on mode — only once per modal open
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    setError(null);
    setSending(false);
    setSavingDraft(false);

    if (mode === 'new') {
      setTo(defaultTo);
      setCc([]);
      setBcc([]);
      setSubject(defaultSubject);
      setBodyHtml('');
      setOpportunityId(defaultOpportunityId);
      setShowCcBcc(false);
    } else if (mode === 'reply' && sourceActivity) {
      const fromEmail = sourceActivity.metadata?.from_email || '';
      setTo(sourceActivity.direction === 'inbound' ? [fromEmail] : (sourceActivity.metadata?.to_emails || []));
      setCc([]);
      setBcc([]);
      const subj = sourceActivity.title || '';
      setSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
      setBodyHtml(_buildQuotedReply(sourceActivity));
      setOpportunityId(sourceActivity.opportunityId || '');
      setShowCcBcc(false);
    } else if (mode === 'replyAll' && sourceActivity) {
      const fromEmail = sourceActivity.metadata?.from_email || '';
      const toEmails = sourceActivity.metadata?.to_emails || [];
      const ccEmails = sourceActivity.metadata?.cc_emails || [];
      if (sourceActivity.direction === 'inbound') {
        setTo([fromEmail]);
        setCc([...toEmails, ...ccEmails]);
      } else {
        setTo(toEmails);
        setCc(ccEmails);
      }
      setBcc([]);
      const subj = sourceActivity.title || '';
      setSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
      setBodyHtml(_buildQuotedReply(sourceActivity));
      setOpportunityId(sourceActivity.opportunityId || '');
      setShowCcBcc(ccEmails.length > 0);
    } else if (mode === 'forward' && sourceActivity) {
      setTo([]);
      setCc([]);
      setBcc([]);
      const subj = sourceActivity.title || '';
      setSubject(subj.startsWith('Fwd:') ? subj : `Fwd: ${subj}`);
      setBodyHtml(_buildForwardedBody(sourceActivity));
      setOpportunityId(sourceActivity.opportunityId || '');
      setShowCcBcc(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSend = async () => {
    if (to.length === 0) {
      setError('At least one recipient is required');
      return;
    }

    setSending(true);
    setError(null);

    try {
      if (mode === 'reply' || mode === 'replyAll') {
        await apiClient.post(`/api/crm-activities/outlook/emails/reply/${sourceActivity!.id}`, {
          body_html: bodyHtml,
          reply_all: mode === 'replyAll',
          opportunity_id: opportunityId || undefined,
        });
      } else if (mode === 'forward') {
        await apiClient.post(`/api/crm-activities/outlook/emails/forward/${sourceActivity!.id}`, {
          to,
          body_html: bodyHtml,
          opportunity_id: opportunityId || undefined,
        });
      } else {
        await apiClient.post('/api/crm-activities/outlook/emails/send', {
          to,
          cc: cc.length > 0 ? cc : undefined,
          bcc: bcc.length > 0 ? bcc : undefined,
          subject,
          body_html: bodyHtml,
          opportunity_id: opportunityId || undefined,
          enable_tracking: trackingEnabled,
        });
      }

      onSent?.('');
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to send email';
      setError(detail);
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setError(null);
    try {
      await apiClient.post('/api/crm-activities/outlook/emails/draft', {
        to: to.length > 0 ? to : undefined,
        cc: cc.length > 0 ? cc : undefined,
        bcc: bcc.length > 0 ? bcc : undefined,
        subject: subject || undefined,
        body_html: bodyHtml || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const modeLabel = useMemo(() => {
    switch (mode) {
      case 'reply': return 'Reply';
      case 'replyAll': return 'Reply All';
      case 'forward': return 'Forward';
      default: return 'New Email';
    }
  }, [mode]);

  const ModeIcon = useMemo(() => {
    switch (mode) {
      case 'reply': return Reply;
      case 'replyAll': return ReplyAll;
      case 'forward': return Forward;
      default: return Send;
    }
  }, [mode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-2xl mx-0 sm:mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ModeIcon className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-900">{modeLabel}</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft || sending}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Save as draft in Outlook"
            >
              {savingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Draft
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Recipients */}
        <div className="px-4 pt-2">
          <RecipientInput label="To" emails={to} onChange={setTo} placeholder="Recipients..." />

          {/* Cc/Bcc toggle */}
          {!showCcBcc ? (
            <button
              onClick={() => setShowCcBcc(true)}
              className="text-xs text-blue-600 hover:text-blue-700 py-1"
            >
              Cc / Bcc
            </button>
          ) : (
            <>
              <RecipientInput label="Cc" emails={cc} onChange={setCc} placeholder="Cc..." />
              <RecipientInput label="Bcc" emails={bcc} onChange={setBcc} placeholder="Bcc..." />
            </>
          )}

          {/* Subject */}
          <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
            <label className="text-xs font-medium text-gray-500 w-8 flex-shrink-0">Subj</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject..."
              className="flex-1 text-sm outline-none bg-transparent"
            />
          </div>

          {/* Link to opportunity */}
          {opportunities.length > 0 && (
            <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
              <label className="text-xs font-medium text-gray-500 w-8 flex-shrink-0">Opp</label>
              <select
                value={opportunityId}
                onChange={(e) => setOpportunityId(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent text-gray-700"
              >
                <option value="">No linked opportunity</option>
                {opportunities.map(o => (
                  <option key={o.id} value={o.id}>{o.name} - {o.company}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Compose toolbar: Templates, AI, Tracking */}
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-100 bg-gray-50/50 overflow-x-auto flex-wrap sm:flex-nowrap">
          {/* Template picker */}
          <div className="relative">
            <button
              onClick={() => { setShowTemplates(!showTemplates); setShowAI(false); if (!showTemplates) loadTemplates(); }}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                showTemplates ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileText className="h-3 w-3" />
              Templates
            </button>
            {showTemplates && (
              <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                {templates.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-gray-400 text-center">No templates yet</p>
                ) : (
                  templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 border-b border-gray-50"
                    >
                      <p className="font-medium text-gray-900">{t.name}</p>
                      {t.subject && <p className="text-xs text-gray-500 truncate">{t.subject}</p>}
                      {t.category && <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 rounded">{t.category}</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Save as template */}
          <button
            onClick={() => setSaveTemplateOpen(!saveTemplateOpen)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Save className="h-3 w-3" />
            Save Template
          </button>

          <div className="h-4 w-px bg-gray-200" />

          {/* AI Assist */}
          <button
            onClick={() => { setShowAI(!showAI); setShowTemplates(false); }}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
              showAI ? 'bg-violet-100 text-violet-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Sparkles className="h-3 w-3" />
            AI Assist
          </button>

          <div className="h-4 w-px bg-gray-200" />

          {/* Tracking toggle */}
          <button
            onClick={() => setTrackingEnabled(!trackingEnabled)}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
              trackingEnabled ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
            title={trackingEnabled ? 'Open & click tracking enabled' : 'Tracking disabled'}
          >
            <Eye className="h-3 w-3" />
            {trackingEnabled ? 'Tracking On' : 'Tracking Off'}
          </button>
        </div>

        {/* Save Template panel */}
        {saveTemplateOpen && (
          <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name..."
                className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-500"
                autoFocus
              />
              <input
                type="text"
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                placeholder="Category..."
                className="w-full sm:w-32 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg"
              />
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
                className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setSaveTemplateOpen(false)}
                className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* AI Assist panel */}
        {showAI && (
          <div className="px-4 py-3 bg-violet-50 border-b border-violet-100">
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Describe what to write... e.g. 'Follow up on our demo last week'"
                  className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-violet-500"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAICompose(); }}
                  autoFocus
                />
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value as any)}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="formal">Formal</option>
                </select>
                <button
                  onClick={handleAICompose}
                  disabled={aiLoading || !aiPrompt.trim()}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                >
                  {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                  Generate
                </button>
              </div>
              {aiResult && (
                <div className="bg-white border border-violet-200 rounded-lg p-3">
                  <div className="text-sm text-gray-700 max-h-32 overflow-y-auto whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: aiResult }} />
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={insertAIResult}
                      className="px-3 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700"
                    >
                      Insert
                    </button>
                    <button
                      onClick={handleAICompose}
                      disabled={aiLoading}
                      className="px-3 py-1 text-xs text-violet-600 bg-violet-50 border border-violet-200 rounded hover:bg-violet-100"
                    >
                      Regenerate
                    </button>
                    <button
                      onClick={() => { setAiResult(''); }}
                      className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rich text body */}
        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[200px]">
          <RichTextEditor
            content={bodyHtml}
            onChange={setBodyHtml}
            placeholder="Compose your email..."
            minHeight="180px"
            showToolbar={true}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-200">
            {error}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              Discard
            </button>
          </div>
          <div className="flex items-center gap-2">
            {trackingEnabled && (
              <span className="flex items-center gap-1 text-[10px] text-green-600">
                <Eye className="h-3 w-3" /> Open tracking
                <MousePointerClick className="h-3 w-3 ml-1" /> Click tracking
              </span>
            )}
            <button
              onClick={handleSend}
              disabled={sending || to.length === 0}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function _buildQuotedReply(activity: Activity): string {
  const from = activity.metadata?.from_email || 'Unknown';
  const date = activity.description ? '' : '';
  const body = activity.description || '';

  return `<br/><br/><div style="border-left: 2px solid #ccc; padding-left: 12px; margin-left: 4px; color: #666;">
<p style="font-size: 12px; color: #999;">On original email from <strong>${from}</strong>:</p>
<div>${body}</div>
</div>`;
}

function _buildForwardedBody(activity: Activity): string {
  const from = activity.metadata?.from_email || '';
  const to = (activity.metadata?.to_emails || []).join(', ');
  const subject = activity.title || '';
  const body = activity.description || '';

  return `<br/><br/><div style="border-top: 1px solid #ccc; padding-top: 12px; margin-top: 8px;">
<p style="font-size: 12px; color: #666;"><strong>---------- Forwarded message ----------</strong><br/>
From: ${from}<br/>
To: ${to}<br/>
Subject: ${subject}</p>
<div>${body}</div>
</div>`;
}
